// MARK://dhk8/storefront/lib/tenant-slug
// Helper: tenantId UUID → tenant slug (humano). Stream 3 Fase 1.
//
// El backend de pos-floor tiene `GET /v1/floor/public/reservations/by-code/:slug/:code`
// que acepta slug humano. La ReservarPage tiene tenantId UUID en la URL —
// para construir el deeplink "consulta tu reserva" necesitamos el slug.
//
// El servicio dhk8-platform-tenants expone:
//   GET /v1/dhk8-platform-tenants/v1/public/branding/by-tenant/:tenantId
// Ese endpoint regresa branding pero no el slug. Como no tenemos un endpoint
// dedicado tenantId→slug aún, usamos heurística:
//   1. La URL pública del consultorio será siempre por slug. Si fallamos, el
//      success screen muestra "consulta con el restaurante" sin link directo.
//   2. Cache module-level del intento (incluso null).
//
// Implementación: probamos a llamar al endpoint `tenants/by-id` (admin context
// no aplica — endpoint público mínimo). Si no existe, regresamos null y el UI
// degrada limpio.

const API_BASE =
  (import.meta as ImportMeta & { env: Record<string, string | undefined> }).env['VITE_API_BASE'] ??
  '/v1'

const cache = new Map<string, Promise<string | null>>()

function isUuid(s: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)
}

async function fetchSlug(tenantId: string): Promise<string | null> {
  if (!isUuid(tenantId)) return null
  try {
    // El branding endpoint regresa { tenantId, displayName, logoUrl, accentColor, slug? }.
    // Si no incluye slug, devolvemos null.
    const res = await fetch(
      `${API_BASE}/dhk8-platform-tenants/v1/public/branding/by-tenant/${encodeURIComponent(tenantId)}`,
      { headers: { accept: 'application/json' } },
    )
    if (!res.ok) return null
    const body = (await res.json()) as
      | { ok: true; data: { slug?: string | null } }
      | { ok: false }
    if (!('ok' in body) || !body.ok) return null
    const slug = body.data?.slug
    if (typeof slug === 'string' && /^[a-z0-9-]+$/.test(slug)) return slug
    return null
  } catch {
    return null
  }
}

export function resolveTenantSlug(tenantId: string): Promise<string | null> {
  const existing = cache.get(tenantId)
  if (existing) return existing
  const p = fetchSlug(tenantId)
  cache.set(tenantId, p)
  return p
}
